// ═══════════════════════════════════════════════════════════════════════════════
//  src/pages/ProfesoresPage.jsx
//  LA VITRINA — ruta /profesores
//
//  Rob: «un lugar donde los alumnos puedan ver a los teachers y contratar a uno
//  de su agrado, eso está abierto a todo el público».
//
//  ── ABIERTA DE VERDAD ───────────────────────────────────────────────────────
//  Sin sesión y sin muro. Es la única pantalla de toda la fase teachers que
//  alguien puede ver antes de tener cuenta, así que es la primera impresión de
//  NSL para quien llega por un enlace de un profesor.
//
//  Por eso no pide registrarse para preguntar: quien quiera clases deja su
//  contacto y ya. Obligar a crear cuenta antes de una pregunta es perder a la
//  mitad de la gente en el sitio donde menos se puede permitir.
//
//  ── EL PRECIO SE VE, Y NO SE CALCULA AQUÍ ───────────────────────────────────
//  Rob: «no se ve cuánto cobra cada teacher; ellos ponen sus precios y nosotros
//  le sumamos un 30% de ganancia», y «puede calcular una clase, 4 clases, 8
//  clases, 12 clases».
//
//  El número que se enseña YA VIENE SUMADO del servidor, y los cuatro paquetes
//  vienen calculados uno por uno. Esta pantalla no multiplica nada: si
//  multiplicara ella, habría dos sitios donde se decide un precio, y el día que
//  se aplique un descuento por volumen sólo cambiaría uno de los dos.
//
//  Lo que cobra el profesor no sale de aquí jamás. El alumno ve UN precio final;
//  el reparto entre el profesor y NSL es asunto interno.
//
//  ── LO QUE NO HACE, Y ES DECISIÓN ───────────────────────────────────────────
//  No cobra, no reserva y no enseña agenda. Contratar es una SOLICITUD que
//  aterriza en el CRM y que Rob resuelve. Meter pagos aquí abriría la pregunta
//  de quién factura la clase, y esa no es una pregunta de esta fase.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './ProfesoresPage.css';
import { enlaceWhatsApp } from '../contacto.js';

const API = `${import.meta.env.VITE_API_URL || ''}/api/vitrina`;

// Un precio con separador de miles. «2340 MXN» y «2,340 MXN» dicen lo mismo,
// pero el primero se lee dos veces.
// Lo que llega al WhatsApp de gerencia. Sin emojis y por el helper compartido:
// el número vive en un solo sitio y el texto pasa por el limpiador, porque los
// emojis llegaban rotos a WhatsApp.
const TEXTO_PROFESOR = `Hola, quiero dar clases de ingles en NSL.

Nombre:
Experiencia dando clases:
Certificaciones (si tienes):
Horarios en los que puedes dar clase:`;

const dinero = (n, moneda) =>
  `${new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n || 0)} ${moneda || 'MXN'}`;

export default function ProfesoresPage() {
  const navigate = useNavigate();
  const [profes, setProfes] = useState(null);
  const [error, setError] = useState('');
  const [elegido, setElegido] = useState(null);

  // ── EL TEMA SE APLICA AQUI ────────────────────────────────────────────────
  // Esta pantalla se puede abrir SIN pasar por ninguna otra —es su gracia—, y
  // entonces nadie ha puesto todavia `data-theme` en el documento. Sin esto se
  // veia siempre oscura aunque el visitante tuviera claro guardado, y se vio
  // pidiendo una captura en claro que salio en negro.
  //
  // El claro por defecto es lo que hacen las demas pantallas publicas
  // (`RegisterPage`, `LoginPage`): quien llega por primera vez no tiene
  // preferencia guardada.
  useEffect(() => {
    let tema = 'light';
    try { tema = localStorage.getItem('theme') || 'light'; } catch { /* modo privado */ }
    document.documentElement.setAttribute('data-theme', tema);
    document.body.setAttribute('data-theme', tema);
  }, []);

  useEffect(() => {
    fetch(`${API}/profesores`)
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(d => setProfes(d.profesores || []))
      .catch(() => setError('No pudimos cargar los profesores. Inténtalo en un momento.'));
  }, []);

  // ── LA SALIDA ─────────────────────────────────────────────────────────────
  // Aquí había un botón a los planes. Rob lo quitó: «esos costos ya no van
  // ahí». Pero una pantalla sin ninguna salida deja a quien entra dependiendo
  // del botón de atrás del navegador, así que queda un camino de vuelta — a la
  // app si tiene sesión, a la portada si no.
  const conSesion = (() => {
    try { return !!localStorage.getItem('token'); } catch { return false; }
  })();

  return (
    <main className="vit">
      <div className="vit-shell">
        <button type="button" className="vit-volver"
                onClick={() => navigate(conSesion ? '/home' : '/')}>
          ← {conSesion ? 'Volver a NSL' : 'Inicio'}
        </button>

        <header className="vit-hero">
          <div className="vit-tag">👩‍🏫 Clases con profesor</div>
          <h1 className="vit-h1">
            <span className="vit-ac">ELIGE</span>
            <span className="vit-glow">TU PROFESOR</span>
          </h1>
          <p className="vit-garantia">
            🛡️ <b>Toda clase contratada aquí la garantiza NSL.</b> Si el profesor
            no se presenta, te la reponemos o te devolvemos lo pagado.
          </p>
        </header>

        {error && <div className="vit-error">{error}</div>}

        {!profes && !error && <div className="vit-cargando">Cargando…</div>}

        {profes?.length === 0 && (
          <div className="vit-vacio">
            <div className="vit-vacio-t">Todavía no hay profesores publicados</div>
            <div className="vit-vacio-s">
              Estamos armando el equipo. Mientras tanto, Spacy no se cansa
              nunca.
            </div>
          </div>
        )}

        <div className="vit-grid">
          {(profes || []).map(p => (
            <article className="vit-card" key={p.id}>
              {/* La inicial en vez de una foto. Ver `siembra_vitrina.py`: una
                  cara es una promesa concreta sobre una persona concreta. */}
              <div className="vit-cara" aria-hidden="true">
                {p.foto_url
                  ? <img src={p.foto_url} alt="" />
                  : <span>{(p.nombre || '?').charAt(0)}</span>}
              </div>

              <h2 className="vit-nombre">{p.nombre}</h2>
              <p className="vit-titular">{p.titular}</p>
              <p className="vit-bio">{p.bio}</p>

              <div className="vit-etiquetas">
                {(p.especialidades || []).map(e => (
                  <span className="vit-etq" key={e}>{e}</span>
                ))}
              </div>

              <div className="vit-pie">
                {p.acento && <span>{p.acento}</span>}
                {p.pais && <span>{p.pais}</span>}
              </div>

              {/* EL PRECIO. Lo primero que busca cualquiera que mira una ficha,
                  y lo único que faltaba. Ya viene con la gestión incluida. */}
              <div className="vit-precio">
                {p.precio > 0 ? (
                  <>
                    <span className="vit-precio-n">{dinero(p.precio, p.moneda)}</span>
                    <span className="vit-precio-u">por hora · precio final</span>
                  </>
                ) : (
                  <span className="vit-precio-u">Precio a consultar</span>
                )}
              </div>

              <button type="button" className="vit-btn"
                      onClick={() => setElegido(p)}>
                Quiero clases con {p.nombre} →
              </button>
            </article>
          ))}
        </div>

        {/* ── SI QUIEN MIRA ES PROFESOR ───────────────────────────────────
            Rob, 22-09-2026: «un botón en la pestaña profesores donde los
            alumnos ven a los teachers, para que me busquen por WhatsApp».

            Va aquí abajo y no arriba a propósito: esta pantalla es para que un
            alumno elija profesor, y una llamada a profesores por delante le
            quitaría el sitio. Quien es profesor llega al final mirando la
            competencia, que es justo cuando se pregunta si podría estar ahí.

            No abre ninguna solicitud en el CRM: es un WhatsApp a gerencia, que
            es como se hace todo lo demás en NSL hoy. */}
        <section className="vit-soy-profe">
          <div>
            <h2 className="vit-soy-t">¿Das clases de inglés?</h2>
            <p className="vit-soy-s">
              Buscamos profesores para el equipo de NSL. Escríbenos y te
              contamos cómo trabajamos, cómo se pagan las clases y qué
              necesitas para empezar.
            </p>
          </div>
          <button
            type="button"
            className="vit-btn vit-soy-btn"
            data-prueba="soy-profesor"
            onClick={() => window.open(enlaceWhatsApp(TEXTO_PROFESOR), '_blank', 'noopener')}
          >
            Quiero dar clases en NSL
          </button>
        </section>
      </div>

      {elegido && (
        <Contratar profe={elegido} onCerrar={() => setElegido(null)} />
      )}
    </main>
  );
}

/* ═════════════════════════════════════════════════════════════════════════════
   LA SOLICITUD

   Elegir cuántas clases, leer las condiciones, aceptarlas y dejar un contacto.

   ── POR QUÉ EL VISTO BUENO NO ES UNA CASILLA CUALQUIERA ─────────────────────
   Lo que se manda al servidor es LA VERSIÓN del documento, no un `true`. Un
   «acepto: sí» no dice qué se aceptó, y el día que cambie una cláusula no
   habría forma de saber quién firmó qué. El servidor además la compara con la
   vigente: aceptar «una versión» no vale, hay que aceptar ESTA.

   Y el total no se manda: se manda cuántas clases. El precio lo pone el
   servidor. Si el total viniera de aquí, pedir clases sería escribir el precio.
   ═════════════════════════════════════════════════════════════════════════ */

function Contratar({ profe, onCerrar }) {
  const paquetes = profe.paquetes || [];

  // Arranca en el segundo paquete (4 clases). Un paquete preseleccionado es
  // una recomendación, y la de una clase suelta no ayuda a nadie: quien quiere
  // probar cambia con un clic.
  const [clases, setClases] = useState(
    paquetes.length ? (paquetes[1]?.clases ?? paquetes[0].clases) : null);
  const [nombre, setNombre] = useState('');
  const [contacto, setContacto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [web, setWeb] = useState('');          // la trampa para robots
  const [acepto, setAcepto] = useState(false);
  const [verLegal, setVerLegal] = useState(false);
  const [legal, setLegal] = useState(null);
  const [legalRoto, setLegalRoto] = useState(false);
  const [estado, setEstado] = useState('escribiendo');
  const [error, setError] = useState('');

  // Las condiciones se piden al servidor en vez de escribirlas aquí: si
  // viviesen en esta pantalla, cambiar una cláusula en el backend dejaría al
  // alumno firmando un texto distinto del que se guarda que firmó.
  useEffect(() => {
    fetch(`${API}/legal/alumno`)
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(setLegal)
      .catch(() => setLegalRoto(true));
  }, []);

  const paquete = paquetes.find(p => p.clases === clases) || null;

  const listo = nombre.trim().length >= 2
    && contacto.trim().length >= 5
    && acepto
    && !!legal?.version;

  const envia = (e) => {
    e.preventDefault();
    if (!listo || estado === 'enviando') return;
    setEstado('enviando');
    setError('');

    const token = localStorage.getItem('token');
    fetch(`${API}/contratar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Si ya tiene sesión, la solicitud queda enlazada a su cuenta. No se
        // le pide entrar: es un extra, no un requisito.
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        teacher_id: profe.id,
        nombre: nombre.trim(),
        contacto: contacto.trim(),
        mensaje: mensaje.trim() || undefined,
        clases: clases || undefined,
        // La versión que se está aceptando, no un «sí».
        acepto_version: legal.version,
        web: web || undefined,
      }),
    })
      .then(r => (r.ok ? r.json() : r.json().then(d => Promise.reject(d))))
      .then(() => setEstado('enviada'))
      .catch(d => {
        setError(typeof d?.detail === 'string'
          ? d.detail
          : 'No se pudo enviar. Inténtalo en un momento.');
        setEstado('escribiendo');
      });
  };

  return (
    <div className="vit-fondo" onClick={onCerrar} role="presentation">
      <div className="vit-hoja" onClick={e => e.stopPropagation()} role="presentation">
        {estado === 'enviada' ? (
          <>
            <h3 className="vit-hoja-t">Listo, {nombre.trim().split(' ')[0]}</h3>
            <p className="vit-hoja-s">
              Le pasamos tu solicitud a {profe.nombre}
              {paquete ? (paquete.clases === 1 ? ' para una clase' : ` para ${paquete.clases} clases`) : ''}.
              Te escribimos a <b>{contacto.trim()}</b> para cuadrar horarios y
              cerrar el pago.
            </p>
            <button type="button" className="vit-btn" onClick={onCerrar}>
              Cerrar
            </button>
          </>
        ) : (
          <form onSubmit={envia}>
            <h3 className="vit-hoja-t">Clases con {profe.nombre}</h3>
            <p className="vit-hoja-s">
              {profe.precio > 0
                ? <>Cuesta <b>{dinero(profe.precio, profe.moneda)}</b> la hora. Ese es el precio final: no hay cargos añadidos después.</>
                : <>Todavía no tiene precio publicado; te lo confirmamos cuando te escribamos.</>}
            </p>

            {/* ── LOS PAQUETES ──────────────────────────────────────────────
                Los cuatro totales vienen ya calculados del servidor. */}
            {paquetes.length > 0 && (
              <div className="vit-campo">
                <span>¿Cuántas clases?</span>
                <div className="vit-paquetes">
                  {paquetes.map(p => (
                    <button type="button" key={p.clases}
                            className={`vit-paq${p.clases === clases ? ' vit-paq-on' : ''}`}
                            aria-pressed={p.clases === clases}
                            onClick={() => setClases(p.clases)}>
                      <b>{p.clases}</b>
                      <i>{p.clases === 1 ? 'clase' : 'clases'}</i>
                      <em>{dinero(p.total, profe.moneda)}</em>
                    </button>
                  ))}
                </div>
                {paquete && (
                  <div className="vit-total">
                    <span>Total</span>
                    <b>{dinero(paquete.total, profe.moneda)}</b>
                  </div>
                )}
              </div>
            )}

            <label className="vit-campo">
              <span>Tu nombre</span>
              <input value={nombre} onChange={e => setNombre(e.target.value)}
                     maxLength={100} autoComplete="name" required />
            </label>

            <label className="vit-campo">
              <span>Correo o WhatsApp</span>
              <input value={contacto} onChange={e => setContacto(e.target.value)}
                     maxLength={160} autoComplete="email" required />
            </label>

            <label className="vit-campo">
              <span>¿Qué quieres trabajar? <i>(opcional)</i></span>
              <textarea value={mensaje} rows={3} maxLength={600}
                        onChange={e => setMensaje(e.target.value)} />
              {/* El contador SIEMPRE a la vista, no sólo al acercarse al
                  límite: un límite que no se puede ver antes de cruzarlo no
                  está puesto para quien escribe. */}
              <small className="vit-contador">{mensaje.length}/600</small>
            </label>

            {/* ── LAS CONDICIONES ───────────────────────────────────────────
                Se leen enteras aquí mismo, sin salir del formulario. Una
                casilla que remite a otra pestaña es una casilla que nadie
                lee. */}
            <div className="vit-legal">
              <div className="vit-legal-cab">
                <b>{legal?.titulo || 'Condiciones de las clases'}</b>
                {legal && (
                  <button type="button" className="vit-legal-ver"
                          onClick={() => setVerLegal(v => !v)}>
                    {verLegal ? 'Ocultar' : 'Leer'}
                  </button>
                )}
              </div>
              {legal?.resumen && <p className="vit-legal-res">{legal.resumen}</p>}

              {verLegal && (legal?.clausulas || []).map(c => (
                <div className="vit-clausula" key={c.titulo}>
                  <b>{c.titulo}</b>
                  <p>{c.texto}</p>
                </div>
              ))}

              {legalRoto && (
                <p className="vit-legal-res">
                  No pudimos cargar las condiciones. Recarga la página: sin
                  poder leerlas no tiene sentido que las aceptes.
                </p>
              )}

              <label className="vit-acepto">
                <input type="checkbox" checked={acepto} disabled={!legal}
                       onChange={e => setAcepto(e.target.checked)} />
                <span>
                  He leído y acepto las condiciones
                  {legal?.version ? <i> (versión {legal.version})</i> : null}
                </span>
              </label>
            </div>

            {/* LA TRAMPA. Oculta para personas, visible para un robot que
                rellena todo lo que encuentra. Cuesta una línea y ahorra la
                mitad del spam sin pedirle nada a quien escribe de verdad — al
                revés que un captcha. */}
            <input className="vit-trampa" tabIndex={-1} autoComplete="off"
                   value={web} onChange={e => setWeb(e.target.value)}
                   aria-hidden="true" />

            {error && <div className="vit-error">{error}</div>}

            <div className="vit-hoja-acciones">
              <button type="submit" className="vit-btn" disabled={!listo || estado === 'enviando'}>
                {estado === 'enviando' ? 'Enviando…' : 'Enviar solicitud'}
              </button>
              <button type="button" className="vit-btn vit-btn-ghost" onClick={onCerrar}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
